import { CollectionPreferencesProps } from '@amzn/awsui-components-react';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import { Preferences } from 'common/ui-helpers';
import { ColumnSetting, VisibleOption } from 'common/utils/config-models';
import { useCallback, useEffect, useState } from 'react';

export interface usePreferencesResult {
  currentPreferences: Preferences;
  onPreferencesConfirmHandler: NonCancelableEventHandler<CollectionPreferencesProps.Preferences<any>>;
}

export interface usePreferencesProps {
  /**
   * Key to store preferences in browser local storage.
   */
  preferencesKey: string;
  /**
   * Default paging size.
   */
  defaultPaging: number;
  /**
   * Columns settings.
   */
  columnsSettings: ColumnSetting[];
  /**
   * Function to refresh data.
   */
  refreshData: () => void;
}

function buildDefaultContentDisplay(columnsSettings: ColumnSetting[]): CollectionPreferencesProps.ContentDisplayItem[] {
  const alwaysCols = columnsSettings
    .filter(col => col.visible === VisibleOption.always)
    .map(col => ({ id: col.id, visible: true }));

  const otherCols = columnsSettings
    .filter(col => col.visible !== VisibleOption.never && col.visible !== VisibleOption.always)
    .map(col => ({ id: col.id, visible: col.visible === VisibleOption.yes }));

  return [...alwaysCols, ...otherCols];
}

export function usePreferences(props: usePreferencesProps) {
  const savedPreferences = localStorage.getItem(props.preferencesKey);
  const initialPreferencesValue = useCallback(() => {
    if (savedPreferences) {
      const parsed = JSON.parse(savedPreferences);
      // Migrate old format: if contentDisplay is missing, build it from visibleContent
      if (!parsed.contentDisplay && parsed.visibleContent) {
        parsed.contentDisplay = buildDefaultContentDisplay(props.columnsSettings).map(item => {
          const colSetting = props.columnsSettings.find(c => c.id === item.id);
          const isAlways = colSetting?.visible === VisibleOption.always;
          return { ...item, visible: isAlways || parsed.visibleContent.includes(item.id) };
        });
      }
      return parsed;
    }
    // Default
    return {
      pageSize: props.defaultPaging,
      stripedRows: true,
      contentDisplay: buildDefaultContentDisplay(props.columnsSettings)
    };
  }, []);
  const [currentPreferences, setCurrentPreferences] = useState<Preferences>(initialPreferencesValue());

  // Prevent early calls of the useEffect to have an effect before they are needed
  const [allowUpdates, setAllowUpdates] = useState(false);

  useEffect(() => {
    setAllowUpdates(true);
    setCurrentPreferences(initialPreferencesValue());
  }, []);

  useEffect(() => {
    if (allowUpdates) {
      props.refreshData();
    }
  }, [currentPreferences]);

  /* istanbul ignore next */
  const onPreferencesConfirmHandler: NonCancelableEventHandler<CollectionPreferencesProps.Preferences<any>> = e => {
    localStorage.setItem(props.preferencesKey, JSON.stringify(e.detail));
    setCurrentPreferences(e.detail);
  };

  return { currentPreferences, onPreferencesConfirmHandler };
}
